import { withHudsonBoardVersions } from '../exerciseBoardModel'
import { DEMO_BOARD_MAP_IDS, mapsFromCatalog } from '../maps'
import { storyAt } from '../storyTime'
import type { ScenarioSession } from '../types'

/**
 * Historical teaching case: US Airways 1549 (Miracle on the Hudson).
 * Approximate positions and a compressed public timeline - not a forensic reconstruction.
 *
 * Story clock: America/New_York, 15:24-15:40 on 2009-01-15.
 *
 * Sources (order-of-magnitude fidelity): NTSB AAR-10/03 public narrative, Wikipedia
 * coordinates for the ditch (~40°46′10″N 74°00′16″W), and the well-known clock
 * from wheels-up (~15:24) through water landing (~15:31). Ferry / shore layout is
 * teaching simplification, not a full marine unit roster.
 *
 * Surface wind (NTSB AAR-10/03 §1.7): Central Park ASOS nearest the ditch -
 * 1451 EST 290°/8 kt, 1551 EST 310°/9 kt; LGA ATIS Papa ~340°/13 kt. Widget uses
 * ~300° from / 10 mph (~9 kt) as a mid-Hudson compromise. VMC, cold, calm water.
 */
const ORIGIN = '2009-01-15T15:24:00-05:00'

const SCRIPT_START_MIN = 15 * 60 + 24
const SCRIPT_END_MIN = 15 * 60 + 40
const SCRIPT_SPAN_MS = (SCRIPT_END_MIN - SCRIPT_START_MIN) * 60_000

/** Wall-clock hm → ms since scenario 0:00 (15:24). */
function at(h: number, m: number, s = 0): number {
  const t = h * 60 + m + s / 60
  return Math.max(0, (t - SCRIPT_START_MIN) * 60_000)
}

/**
 * Approximate departure → Hudson ditch track (teaching sketch, not ADS-B).
 * Early climb uses extra pins so the NE→N bend is a smooth arc (not a sharp V).
 */
const LGA = { lng: -73.874, lat: 40.7769 }
const CLIMB_1 = { lng: -73.868, lat: 40.781 }
const CLIMB_2 = { lng: -73.862, lat: 40.786 }
const CLIMB_3 = { lng: -73.858, lat: 40.792 }
const CLIMB_4 = { lng: -73.862, lat: 40.799 }
const CLIMB_N = { lng: -73.872, lat: 40.806 }
/** Midway climb→strike so the long westbound leg is not 2× faster than neighbors. */
const TO_BIRD = { lng: -73.888, lat: 40.81 }
const BIRD = { lng: -73.905, lat: 40.812 }
const TURN_A = { lng: -73.942, lat: 40.806 }
const TURN_B = { lng: -73.972, lat: 40.796 }
const HUDSON = { lng: -73.992, lat: 40.786 }
const GLIDE = { lng: -74.0, lat: 40.777 }
/**
 * Ditch site - commonly cited mid-Hudson off midtown / Weehawken
 * (~40°46′10″N 74°00′16″W). Path and airplane both end here (overlap hot zone OK;
 * vehicle stack rank keeps the plane pin on top).
 */
const DITCH = { lng: -74.0044, lat: 40.7694 }
/** NY Waterway / Port Imperial side (Weehawken). */
const FERRY_W = { lng: -74.0195, lat: 40.771 }
/** Manhattan west-side ferry approach. */
const FERRY_E = { lng: -73.9995, lat: 40.7665 }
/** USCG / FDNY Marine start (downstream). */
const CG_START = { lng: -74.01, lat: 40.758 }
/** Shore ICP / staging - Weehawken waterfront. */
const ICP = { lng: -74.0225, lat: 40.7698 }
const STAGING = { lng: -74.0238, lat: 40.7712 }
const MUSTER = { lng: -74.0218, lat: 40.7685 }
const EMS = { lng: -74.022, lat: 40.7705 }
const POLICE = { lng: -74.0212, lat: 40.770 }

/**
 * Opening frame: tight on the LGA→ditch path + NJ shore stack, still wide enough
 * to read Manhattan / New Jersey and keep the NE climb tip in frame.
 */
const CAM_OVERVIEW = { lng: -73.942, lat: 40.783, zoom: 11.65 }
/** Mayday - stay wide so the path/Hudson stay in frame; no bird-strike push-in. */
const CAM_MAYDAY = { ...CAM_OVERVIEW }
const CAM_DITCH = { lng: DITCH.lng, lat: DITCH.lat, zoom: 14.2 }
const CAM_BOATS = { lng: -74.006, lat: 40.769, zoom: 13.6 }
const CAM_SHORE_WIDE = { lng: -74.014, lat: 40.7695, zoom: 13.1 }
const CAM_HUDSON_CLOSE = { lng: -74.0044, lat: 40.7694, zoom: 15.1 }

/** Flight path polyline [lng, lat] - LaGuardia → ditch (matches airplane pins). */
const FLIGHT_PATH: [number, number][] = [
  [LGA.lng, LGA.lat],
  [CLIMB_1.lng, CLIMB_1.lat],
  [CLIMB_2.lng, CLIMB_2.lat],
  [CLIMB_3.lng, CLIMB_3.lat],
  [CLIMB_4.lng, CLIMB_4.lat],
  [CLIMB_N.lng, CLIMB_N.lat],
  [TO_BIRD.lng, TO_BIRD.lat],
  [BIRD.lng, BIRD.lat],
  [TURN_A.lng, TURN_A.lat],
  [TURN_B.lng, TURN_B.lat],
  [HUDSON.lng, HUDSON.lat],
  [GLIDE.lng, GLIDE.lat],
  [DITCH.lng, DITCH.lat],
]

export const MIRACLE_HUDSON_ID = 'miracle-hudson-1549'

export function createMiracleHudsonSession(): ScenarioSession {
  const t1524 = at(15, 24)
  const t152430 = at(15, 24, 30)
  const t1525 = at(15, 25)
  const t152530 = at(15, 25, 30)
  const t152545 = at(15, 25, 45)
  const t1526 = at(15, 26)
  const t152630 = at(15, 26, 30)
  const t1527 = at(15, 27)
  const t152740 = at(15, 27, 40)
  const t1528 = at(15, 28)
  const t152820 = at(15, 28, 20)
  const t1529 = at(15, 29)
  const t1530 = at(15, 30)
  const t1531 = at(15, 31)
  const t1532 = at(15, 32)
  const t1535 = at(15, 35)
  const t1537 = at(15, 37)
  const t1540 = at(15, 40)

  const base: ScenarioSession = {
    id: MIRACLE_HUDSON_ID,
    title: 'Miracle on the Hudson',
    intro:
      'Historical teaching case - US Airways 1549, 15 Jan 2009. Positions and times are approximate; the point is the response picture, not a forensic rebuild.',
    summary:
      'Bird strike after LaGuardia takeoff, ditch in the Hudson, ferry and Coast Guard rescue. All 155 survive.',
    storyStartTime: ORIGIN,
    storyEndTime: storyAt(ORIGIN, SCRIPT_SPAN_MS),
    timezone: 'America/New_York',
    showTimeZone: true,
    displayUnits: 'imperial',
    // Exercise keeps the comment track (Walkthrough turns comments off).
    sessionType: 'exercise',
    boardComplexity: 'default',
    maps: mapsFromCatalog(DEMO_BOARD_MAP_IDS),
    activeMapId: 'streets',
    camera: CAM_OVERVIEW,
    cameraBeats: [
      { at: storyAt(ORIGIN, t1524), ...CAM_OVERVIEW },
      // Hold the wide shot through bird strike; color steps yellow→red across the next beats.
      { at: storyAt(ORIGIN, t1528), ...CAM_MAYDAY, eventId: 'ev-mayday' },
      { at: storyAt(ORIGIN, t1531), ...CAM_DITCH, eventId: 'ev-ditch' },
      { at: storyAt(ORIGIN, t1535), ...CAM_BOATS, eventId: 'ev-ferries' },
      { at: storyAt(ORIGIN, t1537), ...CAM_SHORE_WIDE, eventId: 'ev-shore' },
      { at: storyAt(ORIGIN, t1540), ...CAM_HUDSON_CLOSE, eventId: 'ev-close' },
    ],
    comments: [
      {
        id: 'mh-c-depart',
        createdAt: storyAt(ORIGIN, t1524),
        createdAtWallIso: '2009-01-15T20:24:00.000Z',
        authorObjectId: 'icp-1',
        source: 'script',
        body: 'US Airways 1549 departs LaGuardia for Charlotte - VMC, cold, light NW wind ~10 mph.',
      },
      {
        id: 'mh-c-birds',
        createdAt: storyAt(ORIGIN, t1527),
        createdAtWallIso: '2009-01-15T20:27:00.000Z',
        authorObjectId: 'icp-1',
        source: 'script',
        body: 'Bird strike - both engines lose thrust. Crew runs the dual-engine failure checklist.',
      },
      {
        id: 'mh-c-mayday',
        createdAt: storyAt(ORIGIN, t1528),
        createdAtWallIso: '2009-01-15T20:28:00.000Z',
        authorObjectId: 'icp-1',
        source: 'script',
        body: 'Mayday to New York TRACON. Runway options ruled out - ditching in the Hudson.',
      },
      {
        id: 'mh-c-ditch',
        createdAt: storyAt(ORIGIN, t1531),
        createdAtWallIso: '2009-01-15T20:31:00.000Z',
        authorObjectId: 'icp-1',
        source: 'script',
        body: 'Aircraft on the water mid-Hudson. Evacuation onto wings and slides begins.',
      },
      {
        id: 'mh-c-ferry',
        createdAt: storyAt(ORIGIN, t1535),
        createdAtWallIso: '2009-01-15T20:35:00.000Z',
        authorObjectId: 'icp-1',
        source: 'script',
        body: 'NY Waterway ferries on scene - first boats pull people from the wings.',
      },
      {
        id: 'mh-c-close',
        createdAt: storyAt(ORIGIN, t1540),
        createdAtWallIso: '2009-01-15T20:40:00.000Z',
        authorObjectId: 'icp-1',
        source: 'script',
        body: 'All 155 accounted for on the Weehawken shore. No fatalities.',
      },
    ],
    objects: [
      {
        id: 'wind-1',
        type: 'wind',
        label: 'Surface wind',
        scope: 'global',
        pose: null,
        size: 'md',
        description:
          'NTSB AAR-10/03 §1.7 - Central Park ASOS near the ditch (~290-310° at 8-9 kt). Set ~300° from / 10 mph for the board.',
        showOnTimeline: false,
        params: {
          fromDeg: 300,
          speedMph: 10,
          plumeDistanceM: 280,
          plumeSpreadDeg: 22,
        },
        comments: [],
      },
      {
        id: 'flight-path-1',
        type: 'sketch',
        label: 'Flight path',
        scope: 'local',
        pose: LGA,
        size: 'md',
        path: FLIGHT_PATH,
        description:
          'Approximate track: LaGuardia climb, bird-strike turn, south down the Hudson to the ditch site.',
        startsAt: storyAt(ORIGIN, t1524),
        showOnTimeline: false,
        params: { color: '#5B8DEF', width: 3, opacity: 0.85, keepOpacity: true },
        comments: [],
      },
      {
        id: 'airplane-1',
        type: 'airplane',
        label: 'Airplane',
        mapLabel: '1549',
        scope: 'local',
        pose: LGA,
        size: 'lg',
        description:
          'Flight tracker style - continuous glide on a smooth arc. Turns red at bird strike. Holds on the ditch pin at water landing.',
        startsAt: storyAt(ORIGIN, t1524),
        // Author choice: hide mechanics card; narration spine tells the story.
        showOnTimeline: false,
        params: {},
        // Continuous motion; final hold on the ditch pin (overlaps hot zone intentionally).
        // CLIMB_N→BIRD is ~2× the prior hop; give it 60s + a mid pin so ground speed stays even.
        keyframes: [
          { at: storyAt(ORIGIN, t1524), ...LGA },
          { at: storyAt(ORIGIN, t152430), ...CLIMB_1 },
          { at: storyAt(ORIGIN, t1525), ...CLIMB_2 },
          { at: storyAt(ORIGIN, t152530), ...CLIMB_3 },
          { at: storyAt(ORIGIN, t152545), ...CLIMB_4 },
          { at: storyAt(ORIGIN, t1526), ...CLIMB_N },
          { at: storyAt(ORIGIN, t152630), ...TO_BIRD },
          { at: storyAt(ORIGIN, t1527), ...BIRD },
          { at: storyAt(ORIGIN, t152740), ...TURN_A },
          { at: storyAt(ORIGIN, t152820), ...TURN_B },
          { at: storyAt(ORIGIN, t1529), ...HUDSON },
          { at: storyAt(ORIGIN, t1530), ...GLIDE },
          { at: storyAt(ORIGIN, t1531), ...DITCH },
          { at: storyAt(ORIGIN, t1540), ...DITCH },
        ],
        comments: [],
      },
      {
        id: 'ditch-label',
        type: 'label',
        label: 'US Airways 1549',
        scope: 'local',
        pose: { lng: DITCH.lng + 0.0012, lat: DITCH.lat + 0.0008 },
        size: 'lg',
        description: 'Ditch site - mid-Hudson off midtown / Weehawken (approximate).',
        startsAt: storyAt(ORIGIN, t1531),
        showOnTimeline: false,
        params: { text: 'US Airways 1549', color: '#ffffff' },
        comments: [],
      },
      {
        id: 'hot-1',
        type: 'hot_zone',
        label: 'Aircraft keep-out',
        mapLabel: 'Hot zone',
        scope: 'local',
        pose: DITCH,
        size: 'lg',
        description:
          'Keep-out around the ditched aircraft while passengers evacuate onto the wings.',
        startsAt: storyAt(ORIGIN, t1531),
        showOnTimeline: false,
        params: { radiusM: 120 },
        comments: [],
      },
      {
        id: 'ferry-w',
        type: 'boat',
        label: 'NY Waterway (Weehawken)',
        mapLabel: 'NY Waterway',
        scope: 'local',
        pose: FERRY_W,
        size: 'lg',
        description:
          'Ferry from the Weehawken / Port Imperial side - among the first boats alongside.',
        startsAt: storyAt(ORIGIN, t1532),
        showOnTimeline: false,
        params: {},
        keyframes: [
          { at: storyAt(ORIGIN, t1532), ...FERRY_W },
          { at: storyAt(ORIGIN, t1535), lng: DITCH.lng - 0.0008, lat: DITCH.lat + 0.0003 },
        ],
        comments: [],
      },
      {
        id: 'ferry-e',
        type: 'boat',
        label: 'NY Waterway (Manhattan)',
        mapLabel: 'Manhattan ferry',
        scope: 'local',
        pose: FERRY_E,
        size: 'lg',
        description: 'Ferry approaching from the Manhattan west side.',
        startsAt: storyAt(ORIGIN, t1532),
        showOnTimeline: false,
        params: {},
        keyframes: [
          { at: storyAt(ORIGIN, t1532), ...FERRY_E },
          {
            at: storyAt(ORIGIN, t1535 + 30_000),
            lng: DITCH.lng + 0.0009,
            lat: DITCH.lat - 0.0002,
          },
        ],
        comments: [],
      },
      {
        id: 'cg-boat',
        type: 'boat',
        label: 'USCG / FDNY Marine',
        mapLabel: 'Marine',
        scope: 'local',
        pose: CG_START,
        size: 'lg',
        description: 'Second-wave marine response from downriver (USCG / FDNY Marine).',
        startsAt: storyAt(ORIGIN, t1535),
        showOnTimeline: false,
        params: {},
        keyframes: [
          { at: storyAt(ORIGIN, t1535), ...CG_START },
          { at: storyAt(ORIGIN, t1537), lng: DITCH.lng - 0.0003, lat: DITCH.lat - 0.0009 },
        ],
        comments: [],
      },
      {
        id: 'icp-1',
        type: 'icp',
        label: 'Shore ICP',
        mapLabel: 'ICP',
        scope: 'local',
        pose: ICP,
        size: 'md',
        description: 'Shore incident command post on the Weehawken waterfront.',
        startsAt: storyAt(ORIGIN, t1537),
        showOnTimeline: false,
        params: {},
        comments: [],
      },
      {
        id: 'staging-1',
        type: 'staging',
        label: 'Shore staging',
        mapLabel: 'Staging',
        scope: 'local',
        pose: STAGING,
        size: 'md',
        description: 'Boat and EMS staging on the Weehawken side.',
        startsAt: storyAt(ORIGIN, t1537),
        showOnTimeline: false,
        params: {},
        comments: [],
      },
      {
        id: 'muster-1',
        type: 'muster',
        label: 'Muster point',
        mapLabel: 'Muster',
        scope: 'local',
        pose: MUSTER,
        size: 'md',
        description: 'Muster / assembly area for passengers brought ashore.',
        startsAt: storyAt(ORIGIN, t1537),
        showOnTimeline: false,
        params: {},
        comments: [],
      },
      {
        id: 'ems-1',
        type: 'ems',
        label: 'EMS',
        mapLabel: 'EMS',
        scope: 'local',
        pose: EMS,
        size: 'md',
        description: 'Shore EMS for triage and transport.',
        startsAt: storyAt(ORIGIN, t1537),
        showOnTimeline: false,
        params: {},
        comments: [],
      },
      {
        id: 'le-1',
        type: 'police',
        label: 'Law enforcement',
        mapLabel: 'LE',
        scope: 'local',
        pose: POLICE,
        size: 'md',
        description: 'Shore perimeter and traffic control.',
        startsAt: storyAt(ORIGIN, t1537),
        showOnTimeline: false,
        params: {},
        comments: [],
      },
    ],
    timeline: [
      // Focus #3 Hudson pilot: story spine is narration beats. Map presence stays on
      // object startsAt; plane color steps yellow at bird strike, red at the ditch call.
      {
        id: 'ev-depart',
        at: storyAt(ORIGIN, t1524),
        title: 'Depart LaGuardia',
        summary: 'Flight 1549 wheels up for Charlotte. Clear winter afternoon.',
        iconType: 'airplane',
        hiddenFromParticipants: false,
        kind: 'narration',
        actions: [],
      },
      {
        id: 'ev-birds',
        at: storyAt(ORIGIN, t1527),
        title: 'Bird strike',
        summary: 'Canada geese - both engines lose thrust shortly after takeoff.',
        iconType: 'explosion',
        hiddenFromParticipants: false,
        kind: 'narration',
        actions: [
          // Yellow at bird strike (15:27) — plane still near the strike pin.
          { kind: 'setParams', objectId: 'airplane-1', params: { color: '#FAC775' } },
        ],
      },
      {
        id: 'ev-mayday',
        at: storyAt(ORIGIN, t1528),
        title: 'Mayday - ditch decision',
        summary:
          'Crew tells ATC they cannot make a runway. Hudson ditching selected - aircraft still gliding south.',
        iconType: 'airplane',
        hiddenFromParticipants: false,
        kind: 'narration',
        actions: [
          { kind: 'setParams', objectId: 'airplane-1', params: { color: '#E24B4A' } },
        ],
      },
      {
        id: 'ev-ditch',
        at: storyAt(ORIGIN, t1531),
        title: 'Water landing',
        summary: 'Aircraft on the water mid-Hudson at the ditch pin. Hot zone appears.',
        iconType: 'hot_zone',
        hiddenFromParticipants: false,
        kind: 'narration',
        actions: [],
      },
      {
        id: 'ev-evac',
        at: storyAt(ORIGIN, t1532),
        title: 'Evacuate onto wings',
        summary:
          'Passengers and crew onto wings and slides. Ferries already turning toward the aircraft.',
        iconType: 'muster',
        hiddenFromParticipants: false,
        kind: 'narration',
        actions: [],
      },
      {
        id: 'ev-ferries',
        at: storyAt(ORIGIN, t1535),
        title: 'Ferries on scene',
        summary:
          'NY Waterway boats alongside - first people off the wings. Marine unit inbound.',
        iconType: 'boat',
        hiddenFromParticipants: false,
        kind: 'narration',
        actions: [],
      },
      {
        id: 'ev-shore',
        at: storyAt(ORIGIN, t1537),
        title: 'Shore ICP - Weehawken',
        summary: 'ICP, staging, muster, EMS, and LE stand up on the Weehawken waterfront.',
        iconType: 'icp',
        hiddenFromParticipants: false,
        kind: 'narration',
        actions: [],
      },
      {
        id: 'ev-close',
        at: storyAt(ORIGIN, t1540),
        title: 'All accounted for',
        summary: 'All 155 passengers and crew accounted for. No fatalities.',
        iconType: 'airplane',
        hiddenFromParticipants: false,
        kind: 'narration',
        actions: [],
      },
    ],
  }
  // Main (historical) + Sully +30s + bird-strike-later NJ as board versions.
  return withHudsonBoardVersions(base)
}
